import { ACTIVE_RESPONSE_KEYS } from '@/lib/constants/activeResponses'
import { EDR_CONNECTION } from '@/lib/constants/edr'

export const EDR_DASHBOARD = {
  TITLE: 'EDR',
  SUBTITLE: 'Detección y respuesta de endpoints',
  TABS: {
    OVERVIEW: 'Resumen',
    ENDPOINTS: 'Endpoints',
  },
  ENDPOINTS: {
    TITLE: 'Endpoints',
    SUBTITLE: '{count} endpoints en toda tu flota',
    SEARCH_PLACEHOLDER: 'Buscar endpoints...',
    KIND_FILTER: {
      ALL: 'Todos',
      LAPTOPS: 'Portátiles',
      SERVERS: 'Servidores',
    },
    NO_OWNER: 'N/D',
    DRAWER: {
      ALERTS: 'Alertas',
      LAST_30_DAYS: 'Últimos 30d',
      EMPTY: 'No hay eventos para este endpoint',
      EMPTY_ALERTS: 'No hay alertas para este endpoint',
      ERROR: 'No se pudieron cargar las alertas',
      FILTER: {
        ALERTS: 'Alertas',
        ALL: 'Todos los eventos',
      },
      ACTIONS: 'Acciones',
      NETWORK: 'Red',
      CANCEL: 'Cancelar',
      RESTART: 'Reiniciar agente',
      RESTART_CONFIRM_TITLE: '¿Reiniciar el agente en {hostname}?',
      RESTART_CONFIRM_BODY: 'Esto reinicia el agente en el endpoint.',
      RESTART_CONFIRM: 'Reiniciar',
      RESTART_SUCCESS: 'Reinicio solicitado para {hostname}',
      RESTART_ERROR: 'No se pudo reiniciar el agente',
      ISOLATE: 'Aislar',
      ISOLATE_CONFIRM_TITLE: '¿Aislar {hostname} de la red?',
      ISOLATE_CONFIRM_BODY:
        'Esto bloquea todo el tráfico de red del endpoint salvo su conexión con el servidor de monitorización.',
      ISOLATE_CONFIRM: 'Aislar',
      ISOLATE_SUCCESS: 'Aislamiento solicitado para {hostname}',
      ISOLATE_ERROR: 'No se pudo aislar el endpoint',
      RESTORE: 'Restaurar',
      RESTORE_CONFIRM_TITLE: '¿Restaurar el acceso a la red en {hostname}?',
      RESTORE_CONFIRM_BODY: 'Esto levanta el aislamiento y restaura la conectividad de red normal del endpoint.',
      RESTORE_CONFIRM: 'Restaurar',
      RESTORE_SUCCESS: 'Restauración de red solicitada para {hostname}',
      RESTORE_ERROR: 'No se pudo restaurar el acceso a la red',
    },
  },
  KPIS: {
    ALERTS: 'Alertas (30d)',
    ENDPOINTS_AT_RISK: 'Endpoints en riesgo',
    AT_RISK_DEFINITION: 'Endpoints con alertas (30d)',
    MORE: '+{count} más',
    ALL_CLEAR: 'Todo en orden',
    OF_FLEET: '{percent}% de la flota',
    COVERAGE: 'Cobertura',
    COVERAGE_DEFINITION: 'Empleados sin agente instalado',
    RESPONSES: 'Respuestas automáticas (30d)',
  },
  SUMMARY: {
    AGENTS_DEPLOYED: 'agentes desplegados',
    COVERAGE: 'cobertura',
    COVERED: 'Con cobertura',
    NOT_COVERED: 'Sin cobertura',
    VIEW_EMPLOYEES: 'Ver empleados',
    TOOLTIP: {
      [EDR_CONNECTION.ONLINE]: 'El endpoint está en línea',
      [EDR_CONNECTION.OFFLINE]: 'El endpoint está desconectado',
      [EDR_CONNECTION.NEVER]: 'El endpoint nunca se ha conectado',
    } as Record<string, string>,
  },
  TOP_AFFECTED_ENDPOINTS: {
    TITLE: 'Más alertas',
    LAST_DAYS_PREFIX: 'Últimos',
    LAST_DAYS_SUFFIX: 'días',
    EMPTY: 'Sin alertas en los últimos 30 días',
  },
  ALERTS_TREND: {
    TITLE: 'Alertas en el tiempo',
    LAST_DAYS_PREFIX: 'Últimos',
    LAST_DAYS_SUFFIX: 'días',
    CRITICAL: 'Críticas',
    HIGH: 'Altas',
    EMPTY: 'Sin alertas en los últimos 30 días',
  },
  MITRE: {
    TITLE: 'Desglose de amenazas',
    SUBTITLE_PREFIX: 'MITRE ATT&CK ·',
    SUBTITLE_SUFFIX: 'alertas',
    EMPTY: 'Sin datos de desglose de amenazas.',
  },
  RESPONSES: {
    TITLE: 'Respuesta activa',
    SUBTITLE: 'Acciones automáticas activadas por alertas',
    EMPTY: 'Sin respuestas activas en los últimos 30 días',
    SHOW_ALL: 'Ver todas ({count})',
    SHOW_LESS: 'Ver menos',
    TREND: {
      OTHER: 'Otra',
      NONE: 'Sin respuestas',
    },
    DRAWER: {
      RAW_LOG: 'Respuesta sin procesar',
      TIMELINE: 'Cronología',
    },
    // Etiquetas legibles indexadas por ACTIVE_RESPONSE_KEYS (la fuente de verdad de los mecanismos AR).
    // Los mecanismos desconocidos usan la descripción original de la regla.
    ACTIONS: {
      [ACTIVE_RESPONSE_KEYS.ROUTE_NULL_BLOCK]: 'Host aislado de la red',
      [ACTIVE_RESPONSE_KEYS.ROUTE_NULL_UNBLOCK]: 'Host restaurado a la red',
      [ACTIVE_RESPONSE_KEYS.WIN_ROUTE_NULL_BLOCK]: 'Host aislado de la red',
      [ACTIVE_RESPONSE_KEYS.WIN_ROUTE_NULL_UNBLOCK]: 'Host restaurado a la red',
      [ACTIVE_RESPONSE_KEYS.FIREWALL_DROP_BLOCK]: 'Origen bloqueado en el firewall',
      [ACTIVE_RESPONSE_KEYS.FIREWALL_DROP_UNBLOCK]: 'Origen desbloqueado en el firewall',
      [ACTIVE_RESPONSE_KEYS.PF_BLOCK]: 'Origen bloqueado en el firewall',
      [ACTIVE_RESPONSE_KEYS.PF_UNBLOCK]: 'Origen desbloqueado en el firewall',
      [ACTIVE_RESPONSE_KEYS.NPF_BLOCK]: 'Origen bloqueado en el firewall',
      [ACTIVE_RESPONSE_KEYS.NPF_UNBLOCK]: 'Origen desbloqueado en el firewall',
      [ACTIVE_RESPONSE_KEYS.IPFW_BLOCK]: 'Origen bloqueado en el firewall',
      [ACTIVE_RESPONSE_KEYS.IPFW_UNBLOCK]: 'Origen desbloqueado en el firewall',
      [ACTIVE_RESPONSE_KEYS.NETSH_BLOCK]: 'Origen bloqueado en el firewall',
      [ACTIVE_RESPONSE_KEYS.NETSH_UNBLOCK]: 'Origen desbloqueado en el firewall',
      [ACTIVE_RESPONSE_KEYS.HOST_DENY_BLOCK]: 'Host bloqueado',
      [ACTIVE_RESPONSE_KEYS.HOST_DENY_UNBLOCK]: 'Host desbloqueado',
      [ACTIVE_RESPONSE_KEYS.DISABLE_ACCOUNT]: 'Cuenta deshabilitada',
      [ACTIVE_RESPONSE_KEYS.RESTART_WAZUH]: 'Agente reiniciado',
      [ACTIVE_RESPONSE_KEYS.EJECT_USB_MACOS]: 'Unidad USB bloqueada',
    } as Record<string, string>,
  },
  ACTIVITY: {
    TITLE: 'Actividad de endpoints',
    SUBTITLE: 'Endpoints activos más recientemente',
    EMPTY: 'Sin actividad reciente de endpoints',
    ALERTS_COUNT: '{count} alertas',
    AGENT: 'Agente',
    ALERTS: 'Alertas (30d)',
    LAST_SEEN: 'Visto por última vez',
    VERSION: {
      UNKNOWN: 'Desconocida',
      UPDATED: 'Actualizado',
      OUTDATED: 'Desactualizado',
      NO_VERSION: 'Versión del agente no reportada',
      ON_LATEST: 'El agente está en la última versión ({version})',
      RUNNING_OLD: 'El agente está desactualizado: ejecutando {version}',
    },
    LAST_SEEN_FORMAT: {
      JUST_NOW: 'justo ahora',
      MINUTES: 'hace {value}m',
      HOURS: 'hace {value}h',
      DAYS: 'hace {value}d',
      NEVER: '—',
    },
  },
}
